import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { format } from 'date-fns';
import axios from '../utils/axiosConfig';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axios.get(`/api/v2/blog/${id}`);
        setPost(response.data);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        if (err.response?.status === 404) {
          setError('Blog post not found.');
          navigate('/blog');
        } else {
          setError('Failed to load blog post. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, navigate]);

  if (loading) {
    return (
      <Container sx={{ mt: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 10 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/blog')}
          >
            Back to Blog
          </Button>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container sx={{ mt: 10 }}>
        <Alert severity="info">Blog post not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/blog')}
        sx={{ mb: 4 }}
      >
        Back to Blog
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Chip
            label={post.category}
            color="primary"
            size="small"
            sx={{ mb: 2 }}
          />
          <Typography variant="h3" gutterBottom>
            {post.title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              src={post.author?.avatar || '/images/avatars/default.jpg'}
              sx={{ width: 40, height: 40, mr: 2 }}
            />
            <Box>
              <Typography variant="subtitle1">
                {post.author?.name || 'Coach'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(new Date(post.createdAt), 'MMMM d, yyyy')} · {post.readTime} minute read
              </Typography>
            </Box>
          </Box>

          {post.image && (
            <Box
              component="img"
              src={post.image}
              alt={post.title}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '500px',
                objectFit: 'cover',
                borderRadius: 1,
                mb: 4
              }}
            />
          )}

          <Typography variant="subtitle1" color="text.secondary" paragraph>
            {post.summary}
          </Typography>

          <Box
            sx={{ mt: 4 }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default BlogPost;
